/**
 * The live voice translator on the page: tools/_live_translator.html's markup as core.ts's
 * `LiveView`, the browser's pieces handed to `createLiveTranslator`, and the controls wired to
 * it. Mounted by pages/live-translate.ts on [data-live-translator].
 *
 * Every word shown is the template's (the status lines are read from `[data-lt-label]` by key)
 * but the language names, which are the page language's (languages.ts `languageName`), and
 * the limit's sentence (lib/gemini.ts `limitMessage`, lib/i18n.ts). Every visitor's word and
 * every translation is written with `textContent`.
 *
 * MOBILE FIRST. The microphone buttons are tapped, or held in Hold to talk with the pointer
 * captured, so a finger that slides off the button still ends the clip where it is lifted;
 * Space and Enter hold it from a keyboard. Nothing is hover-only, and nothing here needs an
 * API only a computer has: each piece is looked for and its absence handled.
 */

import { copyText } from '../clipboard.ts';
import { qs, qsa } from '../dom.ts';
import { liveTranslate } from '../gemini.ts';
import { type PageLang, pageLang } from '../i18n.ts';
import { type RecognitionCtor, recognitionCtor } from '../live-speech.ts';
import { browserRecorderEnv, saveBlob } from '../mic-panel.ts';
import type { RecorderEnv } from '../recorder.ts';
import { sessionToken } from '../session.ts';
import { showProblem } from '../states.ts';
import { countUse, noteResult, TOOL_SLUG } from '../usage.ts';
import {
  createLiveTranslator,
  type HoldAnswer,
  type HoldClip,
  type Line,
  type LiveTranslator,
  type LiveView,
  type Mode,
  type Side,
  type StatusKey,
} from './core.ts';
import { type DeviceTranslators, deviceTranslators } from './device.ts';
import { createClipSender } from './hold.ts';
import {
  defaultPair,
  type LanguagePair,
  LIVE_CODES,
  languageInText,
  languageName,
} from './languages.ts';
import { createSentenceCount, onPageHide } from './sentence-count.ts';
import { browserSpeechOut, type SpeechOut } from './speech-out.ts';

/** The template's config block (`script[data-tool-config]`), as the contract names its keys. */
export interface LiveTranslatorConfig {
  live_mode?: unknown;
  conversation_tab?: unknown;
  source_default?: unknown;
  target_default?: unknown;
  page_lang?: unknown;
}

export interface MountOptions {
  config?: LiveTranslatorConfig;
  /** The page's banner region, for Hold to talk's refusals and a session that can't be made. */
  region?: HTMLElement | null;
  /** The browser's pieces, replaceable when a test drives the page. */
  recognition?: RecognitionCtor | null;
  device?: DeviceTranslators | null;
  speech?: SpeechOut | null;
  recorderEnv?: RecorderEnv | null;
  sendClip?: (clip: HoldClip) => Promise<HoldAnswer>;
  languages?: readonly string[];
  /** Read aloud's default: on for a phone (a coarse pointer). */
  coarsePointer?: boolean;
  sleep?: (ms: number) => Promise<void>;
}

/** A line's pending translation, while it is on its way. */
const PENDING = '…';

function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  const list = Array.isArray(navigator.languages) ? navigator.languages : [];
  return list.length > 0 ? list : navigator.language ? [navigator.language] : [];
}

function coarse(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/** The recorder's environment, or null where the browser can't record at all. */
function recorderEnvOrNull(): RecorderEnv | null {
  const env = browserRecorderEnv();
  return env.getUserMedia && env.MediaRecorder ? env : null;
}

const MODES: readonly Mode[] = ['speak', 'conversation'];

export function mountLiveTranslator(root: HTMLElement, options: MountOptions = {}): LiveTranslator {
  const config = options.config ?? {};
  const toolAttr = root.dataset['usageTool'] ?? '';
  const tool = TOOL_SLUG.test(toolAttr) ? toolAttr : '';
  const page: PageLang = pageLang();
  const langs = options.languages ?? browserLanguages();

  /* ── the elements ────────────────────────────────────────────────── */

  const tabs = qsa<HTMLButtonElement>('[data-lt-tab]', root);
  const switches = qsa<HTMLButtonElement>('[data-lt-switch]', root);
  const switchRows = qsa<HTMLElement>('[data-lt-switch-row]', root);
  const panes = new Map<Mode, HTMLElement>();
  for (const m of MODES) {
    const pane = qs<HTMLElement>(`[data-lt-pane="${m}"]`, root);
    if (pane) panes.set(m, pane);
  }
  const pickers: Record<Side, HTMLSelectElement[]> = {
    a: qsa<HTMLSelectElement>('[data-lt-lang="a"]', root),
    b: qsa<HTMLSelectElement>('[data-lt-lang="b"]', root),
  };
  const mics: Record<Side, HTMLButtonElement[]> = {
    a: qsa<HTMLButtonElement>('[data-lt-mic="a"]', root),
    b: qsa<HTMLButtonElement>('[data-lt-mic="b"]', root),
  };
  const logs = {
    speak: qs<HTMLElement>('[data-lt-log="speak"]', root),
    a: qs<HTMLElement>('[data-lt-log="a"]', root),
    b: qs<HTMLElement>('[data-lt-log="b"]', root),
  };
  const empties = {
    speak: qs<HTMLElement>('[data-lt-empty="speak"]', root),
    a: qs<HTMLElement>('[data-lt-empty="a"]', root),
    b: qs<HTMLElement>('[data-lt-empty="b"]', root),
  };
  type LogKey = keyof typeof logs;
  const swapBtn = qs<HTMLButtonElement>('[data-lt-swap]', root);
  const faceBtn = qs<HTMLButtonElement>('[data-lt-face]', root);
  const convo = panes.get('conversation') ?? null;
  const statusLine = qs<HTMLElement>('[data-lt-status]', root);
  const pack = qs<HTMLElement>('[data-lt-pack]', root);
  const packBar = qs<HTMLProgressElement>('[data-lt-pack-bar]', root);
  const limitBox = qs<HTMLElement>('[data-lt-limit]', root);
  const limitText = qs<HTMLElement>('[data-lt-limit-text]', root);
  const limitHold = qs<HTMLElement>('[data-lt-limit-hold]', root);
  const useHoldBtn = qs<HTMLButtonElement>('[data-lt-use-hold]', root);
  const voiceLine = qs<HTMLElement>('[data-lt-voice]', root);
  const readBox = qs<HTMLInputElement>('[data-lt-read]', root);
  const copyBtn = qs<HTMLButtonElement>('[data-lt-copy]', root);
  const downloadBtn = qs<HTMLButtonElement>('[data-lt-download]', root);
  const clearBtn = qs<HTMLButtonElement>('[data-lt-clear]', root);
  const wakeLine = qs<HTMLElement>('[data-lt-wake]', root);
  const noneLine = qs<HTMLElement>('[data-lt-none]', root);
  const toolsRow = qs<HTMLElement>('[data-lt-tools]', root);
  const region = options.region ?? document.getElementById('banner-region');

  /** A status line's words, from the template, with `slots` filled where it has them. */
  function label(key: string, slots: Record<string, string> = {}): Node[] {
    const source = qs<HTMLElement>(`[data-lt-label="${key}"]`, root);
    if (!source) return [];
    const nodes = Array.from(source.childNodes, (n) => n.cloneNode(true));
    for (const node of nodes) {
      if (!(node instanceof Element)) continue;
      const own = node.getAttribute('data-slot');
      if (own && own in slots) node.textContent = slots[own] ?? '';
      for (const slot of Array.from(node.querySelectorAll<HTMLElement>('[data-slot]'))) {
        const name = slot.getAttribute('data-slot') ?? '';
        if (name in slots) slot.textContent = slots[name] ?? '';
      }
    }
    return nodes;
  }

  /** Show the children of `scope` whose `attr` is `value`, and hide the rest. */
  const showWhen = (scope: HTMLElement | null, attr: string, value: string) => {
    if (!scope) return;
    for (const el of qsa<HTMLElement>(`[${attr}]`, scope)) {
      el.hidden = el.getAttribute(attr) !== value;
    }
  };

  /* ── the pickers ─────────────────────────────────────────────────── */

  // Each language named in the page's language, in the table's order.
  for (const select of [...pickers.a, ...pickers.b]) {
    const choices = LIVE_CODES.map((code) => {
      const option = document.createElement('option');
      option.value = code;
      option.textContent = languageName(code, page);
      return option;
    });
    select.replaceChildren(...choices);
  }

  /* ── the lines ───────────────────────────────────────────────────── */

  let mode: Mode = 'speak';
  let holding = false;
  /** Neither recognition nor recording here (`unsupported`): no mode is ever shown again. */
  let none = false;
  /** Each log's nodes, by line id. */
  const nodes: Record<LogKey, Map<number, HTMLElement>> = {
    speak: new Map(),
    a: new Map(),
    b: new Map(),
  };
  /** Each log's guess node, while there are words being guessed. */
  const guesses: Partial<Record<LogKey, HTMLElement>> = {};
  /** Which logs a line is drawn in, in the open mode. */
  const logsOf = (): LogKey[] => (mode === 'speak' ? ['speak'] : ['a', 'b']);

  function para(className: string, text: string, lang?: string): HTMLElement {
    const p = document.createElement('p');
    p.className = className;
    p.textContent = text;
    if (lang) p.setAttribute('lang', lang);
    return p;
  }

  /** The line's translation, or what stands in for it: "…", or the note that it failed. */
  function heardPart(line: Line): HTMLElement | null {
    if (line.state === 'done' && line.translation) {
      return para('lt-line__heard', line.translation, line.to);
    }
    if (line.state === 'pending') return para('lt-line__heard lt-line__heard--pending', PENDING);
    if (line.state === 'failed') {
      const note = para('lt-line__note', '');
      note.replaceChildren(...label('line-failed'));
      return note;
    }
    return null;
  }

  function fill(item: HTMLElement, line: Line, key: LogKey): void {
    const said = line.original ? para('lt-line__said', line.original, line.from) : null;
    const heard = heardPart(line);
    // In a half, every line is shown in that half's language first: its own words, or the
    // other person's translated.
    const mine = key !== 'speak' && line.side === key;
    item.className = `lt-line${mine ? ' lt-line--mine' : ''}`;
    if (key === 'speak' || !mine) {
      item.replaceChildren(...[said, heard].filter((n): n is HTMLElement => n !== null));
      return;
    }
    const own = line.original ? para('lt-line__heard', line.original, line.from) : null;
    const theirs =
      line.state === 'done' && line.translation
        ? para('lt-line__said', line.translation, line.to)
        : line.state === 'failed'
          ? heardPart(line)
          : null;
    item.replaceChildren(...[own, theirs].filter((n): n is HTMLElement => n !== null));
  }

  function toBottom(key: LogKey): void {
    const log = logs[key];
    if (log) log.scrollTop = log.scrollHeight;
  }

  function syncEmpty(key: LogKey): void {
    const empty = empties[key];
    if (empty) empty.hidden = nodes[key].size > 0 || guesses[key] !== undefined;
  }

  function drawLine(line: Line): void {
    for (const key of logsOf()) {
      const log = logs[key];
      if (!log) continue;
      let item = nodes[key].get(line.id);
      if (!item) {
        item = document.createElement('li');
        nodes[key].set(line.id, item);
        const guess = guesses[key];
        if (guess) log.insertBefore(item, guess);
        else log.append(item);
      }
      fill(item, line, key);
      syncEmpty(key);
      toBottom(key);
    }
  }

  function redraw(lines: readonly Line[]): void {
    for (const key of Object.keys(logs) as LogKey[]) {
      logs[key]?.replaceChildren();
      nodes[key].clear();
      delete guesses[key];
    }
    for (const line of lines) drawLine(line);
    for (const key of Object.keys(logs) as LogKey[]) syncEmpty(key);
  }

  function drawGuess(side: Side, text: string): void {
    const key: LogKey = mode === 'speak' ? 'speak' : side;
    const log = logs[key];
    if (!log) return;
    let guess = guesses[key];
    if (!text) {
      if (guess) {
        guess.remove();
        delete guesses[key];
        syncEmpty(key);
      }
      return;
    }
    if (!guess) {
      guess = document.createElement('li');
      guess.className = 'lt-line lt-line--guess';
      guesses[key] = guess;
      log.append(guess);
    }
    guess.replaceChildren(para('lt-line__said lt-line__guess', text));
    syncEmpty(key);
    toBottom(key);
  }

  /* ── the view ────────────────────────────────────────────────────── */

  const view: LiveView = {
    mode(next, lines) {
      mode = next;
      for (const [m, pane] of panes) pane.hidden = none || m !== next;
      for (const tab of tabs) {
        const selected = tab.dataset['ltTab'] === next;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      }
      for (const row of switchRows) row.hidden = none || row.dataset['ltSwitchRow'] !== next;
      root.dataset['ltMode'] = next;
      redraw(lines);
    },
    languages(pair: LanguagePair) {
      for (const side of ['a', 'b'] as const) {
        for (const select of pickers[side]) select.value = pair[side];
      }
      for (const side of ['a', 'b'] as const) {
        logs[side]?.setAttribute('lang', pair[side]);
      }
    },
    listening(side) {
      for (const s of ['a', 'b'] as const) {
        const on = side === s;
        for (const button of mics[s]) {
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
          button.classList.toggle('is-listening', on);
          if (!holding) showWhen(button, 'data-lt-when', on ? 'on' : 'off');
        }
      }
      root.classList.toggle('is-listening', side !== null);
    },
    holdMode(on) {
      holding = on;
      for (const button of [...mics.a, ...mics.b]) {
        showWhen(button, 'data-lt-when', on ? 'hold' : 'off');
        button.setAttribute('aria-pressed', 'false');
        button.classList.toggle('lt-mic--hold', on);
      }
      for (const empty of Object.values(empties)) {
        showWhen(empty, 'data-lt-empty-when', on ? 'hold' : 'tap');
      }
    },
    holding(side) {
      for (const s of ['a', 'b'] as const) {
        for (const button of mics[s]) {
          button.classList.toggle('is-holding', side === s);
          button.setAttribute('aria-pressed', side === s ? 'true' : 'false');
        }
      }
      root.classList.toggle('is-listening', side !== null);
    },
    interim: drawGuess,
    line: drawLine,
    drop(line) {
      for (const key of Object.keys(logs) as LogKey[]) {
        const item = nodes[key].get(line.id);
        if (!item) continue;
        item.remove();
        nodes[key].delete(line.id);
        syncEmpty(key);
      }
    },
    status(key: StatusKey | null) {
      statusLine?.replaceChildren(...(key ? label(key) : []));
    },
    noVoice(code) {
      if (!voiceLine) return;
      voiceLine.replaceChildren(...label('no-voice', { language: languageInText(code, page) }));
      voiceLine.hidden = false;
    },
    limit(message, offerHold) {
      if (!limitBox) return;
      limitBox.hidden = message === null;
      if (limitText) limitText.textContent = message ?? '';
      if (limitHold) limitHold.hidden = !offerHold;
    },
    screenLine(show) {
      if (wakeLine) wakeLine.hidden = !show;
    },
    hasText(any) {
      for (const b of [copyBtn, downloadBtn, clearBtn]) if (b) b.disabled = !any;
    },
    readAloud(on) {
      if (readBox) readBox.checked = on;
    },
    faceToFace(on) {
      faceBtn?.setAttribute('aria-pressed', on ? 'true' : 'false');
      convo?.classList.toggle('is-facing', on);
    },
    unsupported() {
      // The one line, and nothing that opens a mode or acts on a session there can't be: the
      // other mode's link, Read aloud, Copy, Download and Clear go with the panes.
      none = true;
      for (const pane of panes.values()) pane.hidden = true;
      for (const tab of tabs) tab.disabled = true;
      for (const row of switchRows) row.hidden = true;
      if (toolsRow) toolsRow.hidden = true;
      if (noneLine) noneLine.hidden = false;
    },
    noSession(problem) {
      // E14, in the page's banner as every page shows it, and counted as every page counts it.
      if (region) showProblem(region, problem);
    },
  };

  /* ── the pieces ──────────────────────────────────────────────────── */

  const recognition =
    options.recognition !== undefined ? options.recognition : recognitionCtor(globalThis);
  const device =
    options.device !== undefined
      ? options.device
      : deviceTranslators(globalThis, {
          onProgress: (loaded) => {
            // The one-time "Downloading the language pack…", while a pack comes down.
            if (pack) pack.hidden = loaded === null;
            if (packBar && loaded !== null) packBar.value = loaded;
          },
        });
  const speech = options.speech !== undefined ? options.speech : browserSpeechOut(globalThis);
  const recorderEnv = options.recorderEnv !== undefined ? options.recorderEnv : recorderEnvOrNull();
  const sendClip =
    options.sendClip ??
    (region
      ? createClipSender({ region, tool })
      : async (): Promise<HoldAnswer> => ({ ok: false }));
  const env = browserRecorderEnv();
  const pair = defaultPair(
    typeof config.source_default === 'string' ? config.source_default : null,
    typeof config.target_default === 'string' ? config.target_default : null,
    langs,
  );
  const openMode: Mode =
    config.live_mode === 'conversation' || root.dataset['ltOpen'] === 'conversation'
      ? 'conversation'
      : 'speak';
  // Counted under the page's slug; a page without one counts nothing.
  const counting = tool !== '';
  // The sentences go out in batches, and what is left of them when the page is hidden or left.
  const sentences = createSentenceCount((engine, source, target, n) =>
    countUse('live_translate_sentence', tool, 'txt', { engine, source, target, sentences: n }),
  );
  if (counting) onPageHide(() => sentences.flush());

  const translator = createLiveTranslator({
    view,
    recognition,
    browserLanguages: langs,
    device,
    server: (text, from, to) => liveTranslate(text, from, to).then((done) => done.text),
    speech,
    recorderEnv,
    sendClip,
    requestWakeLock: env.requestWakeLock,
    onVisible: env.onVisible,
    count: {
      start: (m, source, target) => {
        if (counting) countUse('live_translate_start', tool, 'txt', { mode: m, source, target });
      },
      sentence: (engine, source, target) => {
        if (counting) sentences.add(engine, source, target);
      },
      limit: (scope) => {
        if (counting) countUse('live_translate_limit', tool, 'txt', { scope });
      },
      download: () => {
        if (counting) countUse('live_translate_download', tool, 'txt', {});
      },
    },
    save: saveBlob,
    copy: copyText,
    nameOf: (code) => languageName(code, page),
    warm: () => void sessionToken().catch(() => undefined),
    ...(options.sleep ? { sleep: options.sleep } : {}),
    initial: { mode: openMode, pair, readAloud: options.coarsePointer ?? coarse() },
  });

  /* ── the controls ────────────────────────────────────────────────── */

  for (const tab of tabs) {
    tab.addEventListener('click', () => {
      const m = tab.dataset['ltTab'];
      if (m === 'speak' || m === 'conversation') translator.setMode(m);
    });
    tab.addEventListener('keydown', (ev) => {
      if (ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight') return;
      const next = translator.mode === 'speak' ? 'conversation' : 'speak';
      translator.setMode(next);
      tabs.find((t) => t.dataset['ltTab'] === next)?.focus();
    });
  }
  for (const link of switches) {
    link.addEventListener('click', () => {
      const m = link.dataset['ltSwitch'];
      if (m !== 'speak' && m !== 'conversation') return;
      translator.setMode(m);
      // Focus goes to the mode's own first control, as a tab's would.
      qs<HTMLElement>(`[data-lt-pane="${m}"] select`, root)?.focus();
    });
  }
  for (const side of ['a', 'b'] as const) {
    for (const select of pickers[side]) {
      select.addEventListener('change', () => translator.setLanguage(side, select.value));
    }
    for (const button of mics[side]) wireMic(button, side);
  }
  swapBtn?.addEventListener('click', () => translator.swap());
  faceBtn?.addEventListener('click', () =>
    translator.setFaceToFace(faceBtn.getAttribute('aria-pressed') !== 'true'),
  );
  readBox?.addEventListener('change', () => translator.setReadAloud(readBox.checked));
  useHoldBtn?.addEventListener('click', () => translator.useHold());
  // Copy has no beacon; the rating card hears it all the same (lib/rating-prompt.ts).
  copyBtn?.addEventListener(
    'click',
    () =>
      void translator.copy().then((ok) => {
        if (ok) noteResult(tool, 'copy');
      }),
  );
  downloadBtn?.addEventListener('click', () => translator.download());
  clearBtn?.addEventListener('click', () => {
    translator.clear();
    for (const button of mics.a) if (!button.closest('[hidden]')) button.focus();
  });

  /**
   * A microphone button: a tap switches its side on or off; in Hold to talk it is held. The
   * pointer is captured on press, so lifting the finger anywhere ends the clip.
   */
  function wireMic(button: HTMLButtonElement, side: Side): void {
    let pointer: number | null = null;
    let key = false;
    button.addEventListener('click', () => {
      // A click is the one press every browser lets unlock reading aloud (an iPhone's Safari
      // above all), including the click that ends a held clip.
      speech?.prime();
      if (!translator.holdMode) translator.press(side);
    });
    button.addEventListener('pointerdown', (ev) => {
      if (!translator.holdMode || pointer !== null) return;
      if (ev.button !== undefined && ev.button !== 0) return;
      // No text selection, no long-press menu, no focus ring stealing the press.
      ev.preventDefault();
      pointer = ev.pointerId;
      try {
        button.setPointerCapture?.(ev.pointerId);
      } catch {
        /* an engine without capture still sends pointerup to the button */
      }
      translator.holdStart(side);
    });
    const lift = (ev: PointerEvent) => {
      if (pointer === null || ev.pointerId !== pointer) return;
      pointer = null;
      translator.holdEnd(side);
    };
    button.addEventListener('pointerup', lift);
    button.addEventListener('pointercancel', lift);
    button.addEventListener('lostpointercapture', lift);
    button.addEventListener('contextmenu', (ev) => {
      if (translator.holdMode) ev.preventDefault();
    });
    button.addEventListener('keydown', (ev) => {
      if (!translator.holdMode || (ev.key !== ' ' && ev.key !== 'Enter')) return;
      ev.preventDefault();
      if (ev.repeat || key) return;
      key = true;
      speech?.prime();
      translator.holdStart(side);
    });
    button.addEventListener('keyup', (ev) => {
      if (!key || (ev.key !== ' ' && ev.key !== 'Enter')) return;
      ev.preventDefault();
      key = false;
      translator.holdEnd(side);
    });
    button.addEventListener('blur', () => {
      if (!key) return;
      key = false;
      translator.holdEnd(side);
    });
  }

  return translator;
}
